import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useParams } from 'react-router-dom';
import { createPatient, updatePatientCreation } from '../../../services/patient/creation';
import DateInput from '../../../components/DateInput';
import Loading from '../../../components/Loading';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import { usePractice } from '../../../providers/practices/PracticesProvider';
import { useToast } from '../../../providers/ToastProvider';
import { reportCaughtError } from '../../../services/errorReporting';
import { isValidEmail, isValidPhone } from '../../../utils/contact';

const PatientDetails: React.FC = () => {
    const { goNext } = useStepperContext();
    const { setFooterContent } = useLayoutFooter();
    const { formData, updateSubmissionForm, initNewSubmissionForm, isLoading } =
        useSubmissionForm();
    const { practices } = usePractice();
    const { showToast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const { id } = useParams<{ id: string }>();

    const hasMultiplePractices = practices.length > 1;

    useEffect(() => {
        // Only initialize a new empty form when creating a new patient (no id param)
        // When editing an existing patient, the form data will be loaded from the provider
        if (!id && !formData) {
            initNewSubmissionForm();
        }
    }, [formData, initNewSubmissionForm, id]);

    // Auto-select practice if there's only one
    useEffect(() => {
        if (practices.length === 1 && formData && !formData.practiceId) {
            updateSubmissionForm({ practiceId: practices[0].id ?? '' });
        }
    }, [practices, formData, updateSubmissionForm]);

    const isDisabled = useMemo(() => {
        if (!formData) return true;
        return (
            !formData.practiceId?.trim() ||
            !formData.firstName?.trim() ||
            !formData.lastName?.trim() ||
            !formData.gender?.trim() ||
            !formData.dateOfBirth?.trim() ||
            !formData.email?.trim() ||
            !isValidEmail(formData.email) ||
            !formData.phone?.trim() ||
            !isValidPhone(formData.phone)
        );
    }, [formData]);

    const patientDetails = useMemo(
        () => ({
            practiceId: formData?.practiceId ?? '',
            firstName: formData?.firstName ?? '',
            lastName: formData?.lastName ?? '',
            email: formData?.email ?? '',
            phone: formData?.phone ?? '',
            dateOfBirth: formData?.dateOfBirth ?? '',
            gender: formData?.gender ?? ''
        }),
        [formData]
    );

    const handleInputChange = useCallback(
        (field: string, value: string) => {
            setIsModified(true);
            updateSubmissionForm({ [field]: value } as any);
        },
        [updateSubmissionForm]
    );

    const commitFormData = useCallback(async () => {
        setIsSaving(true);
        try {
            let patientId = id;
            if (isModified) {
                const payload = {
                    practiceId: formData?.practiceId ?? '',
                    firstName: formData?.firstName ?? '',
                    lastName: formData?.lastName ?? '',
                    gender: formData?.gender as 'Male' | 'Female',
                    dateOfBirth: formData?.dateOfBirth ?? '',
                    email: formData?.email ?? '',
                    phone: formData?.phone ?? '',
                    countryShortName: formData?.phoneCountry as 'US' | 'CA' | undefined
                };
                const result = id
                    ? await updatePatientCreation(id, payload)
                    : await createPatient(payload);
                patientId = result.id;
                updateSubmissionForm({ id: patientId });
                setIsModified(false);
            }
            return patientId;
        } finally {
            setIsSaving(false);
        }
    }, [updateSubmissionForm, id, formData, isModified]);

    const handleNextClick = useCallback(async () => {
        if (isDisabled) {
            showToast('Please fill in all required fields', 'error');
            return;
        }
        try {
            const newId = await commitFormData();
            goNext(newId);
        } catch (error) {
            showToast('Failed to save patient details. Please try again.', 'error');
            reportCaughtError(error);
        }
    }, [commitFormData, goNext, isDisabled, showToast]);

    useEffect(() => {
        setFooterContent(
            <div className="w-full flex justify-end items-center gap-3">
                <MessageOdoFooterButton />
                <button
                    disabled={isDisabled || isSaving}
                    onClick={handleNextClick}
                    className={`flex flex-row justify-center items-center px-4 py-3.5 gap-2 w-24 h-10 rounded-lg font-bold text-sm leading-4 text-center tracking-wider ${
                        isDisabled || isSaving
                            ? 'bg-gray-300 text-white cursor-not-allowed'
                            : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                >
                    Continue
                </button>
            </div>
        );

        return () => {
            setFooterContent(null);
        };
    }, [setFooterContent, isSaving, handleNextClick, isDisabled]);

    // Show loading state when editing an existing patient and data is still loading
    if (id && isLoading) {
        return <Loading text="Loading patient data..." />;
    }

    return (
        <>
            {isSaving && <Loading text="Saving..." />}
            <div className="max-w-[40%] mx-auto">
                <div className="flex flex-row justify-center items-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-8">Patient Details</h1>
                </div>

                <div className="space-y-6">
                    <label className="block text-sm font-medium text-gray-700">
                        Please enter the patient's basic information
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name *
                            </label>
                            <input
                                type="text"
                                value={patientDetails.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="ex. John"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name *
                            </label>
                            <input
                                type="text"
                                value={patientDetails.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="ex. Smith"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Gender *
                            </label>
                            <select
                                value={patientDetails.gender}
                                onChange={(e) => handleInputChange('gender', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                            </select>
                        </div>

                        <DateInput
                            value={patientDetails.dateOfBirth}
                            onChange={(value) => handleInputChange('dateOfBirth', value)}
                            label="Date of Birth"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone *
                        </label>
                        <div className="w-full">
                            <PhoneInput
                                country={'us'}
                                onlyCountries={['us', 'ca']}
                                disableDropdown
                                countryCodeEditable={false}
                                value={(patientDetails.phone || '').replace(/^\+/, '')}
                                onChange={(value, country) => {
                                    const normalized = value.startsWith('+') ? value : `+${value}`;
                                    const countryCode = (
                                        country as { countryCode?: string }
                                    )?.countryCode?.toUpperCase();
                                    setIsModified(true);
                                    updateSubmissionForm({
                                        phone: normalized,
                                        phoneCountry: countryCode
                                    });
                                }}
                                inputStyle={{ width: '100%', height: '40px' }}
                                inputClass={`${patientDetails.phone && !isValidPhone(patientDetails.phone) ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'} px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                placeholder="(201) 555-0123"
                            />
                        </div>
                        {patientDetails.phone && !isValidPhone(patientDetails.phone) && (
                            <p className="text-red-500 text-sm mt-1">
                                Please enter a valid US or Canadian phone number
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            value={patientDetails.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                patientDetails.email && !isValidEmail(patientDetails.email)
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300'
                            }`}
                            placeholder="you@email.com"
                        />
                        {patientDetails.email && !isValidEmail(patientDetails.email) && (
                            <p className="text-red-500 text-sm mt-1">
                                Please enter a valid email address
                            </p>
                        )}
                    </div>

                    {hasMultiplePractices && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Practice *
                            </label>
                            <select
                                value={patientDetails.practiceId}
                                onChange={(e) => handleInputChange('practiceId', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Practice</option>
                                {practices.map((practice) => (
                                    <option key={practice.id ?? ''} value={practice.id ?? ''}>
                                        {practice.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default PatientDetails;
